import {
    ArrowTopRightOnSquareIcon,
    CalendarDaysIcon,
    PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { Avatar } from '../../../../components/Avatar';
import CopiableText from '../../../../components/CopiableText';
import { formatPhone } from '../../../../utils/contact';
import { calculateAge, formatDate } from '../../../../utils/time';
import { getCaseType, getSubCaseType } from '../../CreationProcess/treatmentDetails';
import { MANUFACTURERS } from '../../../../utils/constants';

const DM_APP_ACTIVATED_VALUE = 'patient app activated';

interface PatientHeaderCardProps {
    // Identity
    name: string | null;
    email: string | null;
    phone: string | null;
    dateOfBirth: string | null;
    caseId?: string | null;
    profilePicUrl?: string | null;
    // Overview
    caseType?: string | null;
    subCaseType?: string | null;
    manufacturer?: string | null;
    manufacturerLink?: string | null;
    duration?: string | null;
    /**
     * When provided, replaces the "Duration" field with a "DM App" activation
     * field (dot + label) instead. Leave undefined to keep showing Duration.
     */
    dmAppActivationStatus?: string | null;
    /** Sends the DM app activation link to the patient. Only rendered when
     *  the app is not yet activated; omit to hide the send-link button. */
    onSendActivationLink?: () => void;
    /** Disables the send-link button and swaps its icon for a spinner. */
    isSendingActivationLink?: boolean;
    /** Denser layout (smaller avatar, tighter gaps) for narrow sidebars. */
    compact?: boolean;
}

/**
 * Combined identity + overview card for the Approve page sidebar.
 * Top row: avatar | name (+ DOB) + contact info.
 * Bottom: 2×2 grid of Case type / Approach / Duration (or DM App) / Manufacturer.
 */
export function PatientHeaderCard({
    name,
    email,
    phone,
    dateOfBirth,
    caseId,
    profilePicUrl,
    caseType,
    subCaseType,
    manufacturer,
    manufacturerLink,
    duration,
    dmAppActivationStatus,
    onSendActivationLink,
    isSendingActivationLink = false,
    compact = false
}: PatientHeaderCardProps) {
    const dobLine = dateOfBirth
        ? `DOB ${formatDate(dateOfBirth)} · ${calculateAge(dateOfBirth)} yrs`
        : 'DOB -';

    const caseLabel = caseType ? (getCaseType(caseType)?.label ?? caseType) : '-';
    const approachLabel =
        caseType && subCaseType
            ? (getSubCaseType(caseType, subCaseType)?.title ?? subCaseType)
            : '-';
    const durationLabel = duration
        ? `${duration}${duration.includes('months') ? '' : ' months'}`
        : '-';
    const showDmAppField = dmAppActivationStatus !== undefined;
    const manufacturerEntry = MANUFACTURERS.find((m) => m.id === manufacturer);

    return (
        <div
            className={`bg-white border border-gray-200 rounded-xl flex-shrink-0 flex flex-col ${compact ? 'p-3 gap-3' : 'p-4 gap-4'}`}
        >
            {/* Header - avatar + name + small caption (DOB · age · case#) */}
            <div className={`flex items-center ${compact ? 'gap-3' : 'gap-3.5'}`}>
                <div className={`flex-shrink-0 ${compact ? 'w-9 h-9' : 'w-12 h-12'}`}>
                    <Avatar
                        user={{ name: name ?? '', email: email ?? '' }}
                        imageURL={profilePicUrl ?? ''}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className={`font-bold text-gray-900 truncate ${compact ? 'text-sm' : 'text-base'}`}
                    >
                        {name ?? '-'}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                        {dobLine}
                        {caseId ? ` · #${caseId}` : ''}
                    </div>
                </div>
            </div>

            {/* Unified grid - contact + overview together, no divider */}
            <div className={`grid grid-cols-2 ${compact ? 'gap-x-4 gap-y-2' : 'gap-x-5 gap-y-3'}`}>
                <Field label="Case type" value={caseLabel} />
                <Field label="Approach" value={approachLabel} />
                {showDmAppField ? (
                    <DmAppActivationField
                        status={dmAppActivationStatus ?? null}
                        onSendActivationLink={onSendActivationLink}
                        isSending={isSendingActivationLink}
                    />
                ) : (
                    <Field
                        label="Duration"
                        value={durationLabel}
                        icon={<CalendarDaysIcon className="w-3.5 h-3.5 text-gray-400" />}
                    />
                )}
                <ManufacturerField entry={manufacturerEntry} link={manufacturerLink ?? null} />
                {email && <EmailField value={email} />}
                {phone && <Field label="Phone" value={formatPhone(phone)} />}
            </div>
        </div>
    );
}

function EmailField({ value }: { value: string }) {
    return (
        <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                Email
            </div>
            <div className="text-sm text-blue-600 truncate">
                <CopiableText value={value} />
            </div>
        </div>
    );
}

function Field({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                {icon}
                <span>{label}</span>
            </div>
            <div className="text-sm text-gray-900 truncate">{value}</div>
        </div>
    );
}

interface DmAppActivationFieldProps {
    status: string | null;
    onSendActivationLink?: () => void;
    isSending?: boolean;
}

function DmAppActivationField({
    status,
    onSendActivationLink,
    isSending = false
}: DmAppActivationFieldProps) {
    const isActivated = status?.toLowerCase() === DM_APP_ACTIVATED_VALUE;
    const dot = isActivated ? 'bg-emerald-500' : 'bg-red-500';
    const text = isActivated ? 'text-emerald-700' : 'text-red-700';
    const label = isActivated ? 'App activated' : 'App not activated';
    const showSendButton = !isActivated && !!onSendActivationLink;

    return (
        <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                DM App
            </div>
            <div className={`flex items-center gap-1.5 text-sm truncate ${text}`}>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                {label}
                {showSendButton && (
                    <Tooltip content="Send app activation link">
                        <button
                            type="button"
                            onClick={onSendActivationLink}
                            disabled={isSending}
                            aria-label="Send app activation link"
                            className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSending ? (
                                <span className="block h-3.5 w-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            ) : (
                                <PaperAirplaneIcon className="w-3.5 h-3.5 -rotate-45" />
                            )}
                        </button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}

interface ManufacturerFieldProps {
    entry: (typeof MANUFACTURERS)[number] | undefined;
    link: string | null;
}

function ManufacturerField({ entry, link }: ManufacturerFieldProps) {
    return (
        <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                Manufacturer
            </div>
            {entry ? (
                link ? (
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 hover:opacity-70 transition-opacity"
                    >
                        <img src={entry.logo} alt={entry.name} className="h-5 w-auto" />
                        <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-400" />
                    </a>
                ) : (
                    <div className="inline-flex items-center opacity-60">
                        <img src={entry.logo} alt={entry.name} className="h-5 w-auto" />
                    </div>
                )
            ) : (
                <div className="text-sm text-gray-500">-</div>
            )}
        </div>
    );
}
